import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { Group, GroupMember } from '@types'
import { groupsApi } from '@api/groups'

interface GroupsState {
  groups: Group[]
  selectedGroup: Group | null
  loadingState: 'idle' | 'pending' | 'succeeded' | 'failed'
  error: string | null
}

const initialState: GroupsState = {
  groups: [],
  selectedGroup: null,
  loadingState: 'idle',
  error: null,
}

// Async thunks
export const fetchGroups = createAsyncThunk<Group[]>(
  'groups/fetchGroups',
  async () => {
    const response = await groupsApi.getGroups()
    return response.data.data
  }
)

export const fetchGroup = createAsyncThunk<Group, number>(
  'groups/fetchGroup',
  async (id: number) => {
    const response = await groupsApi.getGroup(id)
    return response.data.data
  }
)

export const createGroup = createAsyncThunk<Group, Partial<Group>>(
  'groups/createGroup',
  async (groupData: Partial<Group>) => {
    const response = await groupsApi.createGroup(groupData)
    return response.data.data
  }
)

export const updateGroup = createAsyncThunk<Group, { id: number; data: Partial<Group> }>(
  'groups/updateGroup',
  async ({ id, data }: { id: number; data: Partial<Group> }) => {
    const response = await groupsApi.updateGroup(id, data)
    return response.data.data
  }
)

export const deleteGroup = createAsyncThunk(
  'groups/deleteGroup',
  async (id: number) => {
    await groupsApi.deleteGroup(id)
    return id
  }
)

export const inviteUserToGroup = createAsyncThunk(
  'groups/inviteUserToGroup',
  async ({ groupId, email }: { groupId: number; email: string }) => {
    const response = await groupsApi.inviteUser(groupId, email)
    return response.data
  }
)

// `membershipId` is the id of the Group.members[] entry (a GroupMembership
// row), which is what the backend's manage_member action expects.
export const removeUserFromGroup = createAsyncThunk(
  'groups/removeUserFromGroup',
  async ({ groupId, membershipId }: { groupId: number; membershipId: number }) => {
    await groupsApi.removeUser(groupId, membershipId)
    return { groupId, membershipId }
  }
)

export const joinGroup = createAsyncThunk(
  'groups/joinGroup',
  async ({ groupId, message }: { groupId: number; message?: string }) => {
    await groupsApi.joinGroup(groupId, message)
    // join/ returns no group body, so re-read it for the store.
    const response = await groupsApi.getGroup(groupId)
    return response.data.data
  }
)

export const leaveGroup = createAsyncThunk(
  'groups/leaveGroup',
  async (groupId: number) => {
    await groupsApi.leaveGroup(groupId)
    return groupId
  }
)

const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    setSelectedGroup: (state, action: PayloadAction<Group | null>) => {
      state.selectedGroup = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch groups
      .addCase(fetchGroups.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.groups = action.payload
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.error.message || 'Failed to fetch groups'
      })

      // Fetch single group
      .addCase(fetchGroup.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(fetchGroup.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.selectedGroup = action.payload
        
        // Update in groups array if exists
        const index = state.groups.findIndex((g: Group) => g.id === action.payload.id)
        if (index !== -1) {
          state.groups[index] = action.payload
        }
      })
      .addCase(fetchGroup.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.error.message || 'Failed to fetch group'
      })

      // Create group
      .addCase(createGroup.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.groups.push(action.payload)
        state.selectedGroup = action.payload
      })
      .addCase(createGroup.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.error.message || 'Failed to create group'
      })

      // Update group
      .addCase(updateGroup.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        const index = state.groups.findIndex((g: Group) => g.id === action.payload.id)
        if (index !== -1) {
          state.groups[index] = action.payload
        }
        if (state.selectedGroup?.id === action.payload.id) {
          state.selectedGroup = action.payload
        }
      })
      .addCase(updateGroup.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.error.message || 'Failed to update group'
      })

      // Delete group
      .addCase(deleteGroup.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.groups = state.groups.filter((g: Group) => g.id !== action.payload)
        if (state.selectedGroup?.id === action.payload) {
          state.selectedGroup = null
        }
      })
      .addCase(deleteGroup.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.error.message || 'Failed to delete group'
      })

      // Invite user
      .addCase(inviteUserToGroup.pending, (state) => {
        state.error = null
      })
      .addCase(inviteUserToGroup.fulfilled, (state, action) => {
        // Refresh the selected group to get updated members
        if (state.selectedGroup) {
          state.selectedGroup = { ...state.selectedGroup, ...action.payload }
        }
      })
      .addCase(inviteUserToGroup.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to invite user'
      })

      // Remove user
      .addCase(removeUserFromGroup.pending, (state) => {
        state.error = null
      })
      .addCase(removeUserFromGroup.fulfilled, (state, action) => {
        const { groupId, membershipId } = action.payload
        if (state.selectedGroup?.id === groupId) {
          state.selectedGroup.members = state.selectedGroup.members?.filter((m: GroupMember) => m.id !== membershipId) || []
        }
      })
      .addCase(removeUserFromGroup.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to remove user'
      })

      // Join group
      .addCase(joinGroup.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(joinGroup.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.groups.push(action.payload)
      })
      .addCase(joinGroup.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.error.message || 'Failed to join group'
      })

      // Leave group
      .addCase(leaveGroup.pending, (state) => {
        state.loadingState = 'pending'
        state.error = null
      })
      .addCase(leaveGroup.fulfilled, (state, action) => {
        state.loadingState = 'succeeded'
        state.groups = state.groups.filter((g: Group) => g.id !== action.payload)
        if (state.selectedGroup?.id === action.payload) {
          state.selectedGroup = null
        }
      })
      .addCase(leaveGroup.rejected, (state, action) => {
        state.loadingState = 'failed'
        state.error = action.error.message || 'Failed to leave group'
      })
  },
})

export const { setSelectedGroup, clearError } = groupsSlice.actions
export default groupsSlice.reducer
